import React, { useState } from 'react';

import { Button, Table, Icon, Grid, Header } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';

import { flagBusinessSaved } from 'actions/business';
import ConfirmModal from 'components/common/ConfirmModal';
import { useQuestionsContext } from 'components/modules/questions/context';

import GroupEditModal from './components/GroupEditModal';

import './QuestionGroupsTab.scss';

const propTypes = {};
const defaultProps = {};

export default function QuestionGroupsTab() {
  const {
    groups,
    questions,
    refreshQuestions,
    createGroup,
    updateGroup,
    deleteGroup,
    loading: groupsLoading,
    showMessage,
  } = useQuestionsContext();

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const dispatch = useDispatch();

  function getQuestionCount(groupId) {
    const matchingGroup = groups.find((group) => group.id === groupId);
    return _get(matchingGroup, 'questions', []).length;
  }

  function onAddGroup() {
    setEditingGroup(null);
    setShowEditModal(true);
  }

  function onEditGroup(group) {
    setEditingGroup(group);
    setShowEditModal(true);
  }

  async function onSaveGroupEdit(updatedGroup, updatedQuestions) {
    setSaving(true);
    try {
      const orderedQuestions = updatedQuestions.map((q) => q.id);
      if (editingGroup === null) {
        await createGroup({
          name: updatedGroup.name,
          orderedQuestions,
        });
      } else {
        await updateGroup(updatedGroup.id, {
          name: updatedGroup.name,
          orderedQuestions,
        });
      }
      await refreshQuestions();
      showMessage('Group saved successfully', 'success');
      setShowEditModal(false);
      setEditingGroup(null);
      dispatch(flagBusinessSaved(true));
    } catch (e) {
      showMessage('Failed to save group', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function onDeleteGroup(groupId) {
    setSaving(true);
    try {
      await deleteGroup(groupId);
      await refreshQuestions();
      showMessage('Group deleted successfully', 'success');
      setGroupToDelete(null);
    } catch (e) {
      showMessage('Failed to delete group', 'error');
    } finally {
      setSaving(false);
    }
  }

  function onConfirmDeleteGroup() {
    if (groupToDelete) {
      onDeleteGroup(groupToDelete);
    }
  }

  return (
    <div className="question-groups-tab">
      <Grid>
        <Grid.Row>
          <Grid.Column width={12}>
            <div className="tab-header">
              <Header as="h3">Manage Question Groups</Header>
              <p>Organize your questions into groups for better management and categorization.</p>
            </div>
          </Grid.Column>
          <Grid.Column width={4} textAlign="right">
            <Button
              className="secondary-navy"
              icon="plus"
              content="Add Group"
              onClick={onAddGroup}
              disabled={saving || groupsLoading}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {groupsLoading && (
        <div className="loading-state">
          <Icon name="spinner" loading /> Loading groups...
        </div>
      )}
      {saving && (
        <div className="loading-state">
          <Icon name="spinner" loading /> Saving changes...
        </div>
      )}

      {_isEmpty(groups) ? (
        <div className="empty-state">
          <Header as="h3">No groups found</Header>
          <p>Create your first group to start organizing your questions.</p>
        </div>
      ) : (
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Group Name</Table.HeaderCell>
              <Table.HeaderCell>Questions Count</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {groups.map((group) => (
              <Table.Row key={group.id}>
                <Table.Cell>
                  <strong>{group.name}</strong>
                </Table.Cell>
                <Table.Cell>
                  {getQuestionCount(group.id)} question{getQuestionCount(group.id) !== 1 ? 's' : ''}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    className="action-button"
                    icon="edit"
                    content="Edit"
                    onClick={() => onEditGroup(group)}
                    disabled={saving || groupsLoading}
                  />
                  <ConfirmModal
                    trigger={
                      <Button
                        className="delete-red"
                        icon="trash"
                        content="Delete"
                        onClick={() => setGroupToDelete(group.id)}
                        disabled={saving || groupsLoading}
                      />
                    }
                    open={groupToDelete === group.id}
                    header="Delete Group"
                    content={`Are you sure you want to delete "${group.name}"? This action cannot be undone.`}
                    confirmButton={<Button className="delete-red" content="Delete" />}
                    cancelButton={<Button className="action-button-gray" content="Cancel" />}
                    onCancel={() => setGroupToDelete(null)}
                    onConfirm={onConfirmDeleteGroup}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {showEditModal && (
        <GroupEditModal
          open={showEditModal}
          group={editingGroup}
          allQuestions={questions}
          saving={saving}
          onClose={() => {
            setShowEditModal(false);
            setEditingGroup(null);
          }}
          onSave={onSaveGroupEdit}
        />
      )}
    </div>
  );
}

QuestionGroupsTab.propTypes = propTypes;
QuestionGroupsTab.defaultProps = defaultProps;
